import { DownOutlined, EllipsisOutlined, LoadingOutlined, UpOutlined } from '@ant-design/icons';
import { Card, Dropdown, theme, Typography } from 'antd';
import moment from 'moment';
import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useTimestampLabel } from 'src/common/hooks/use-timestamp-label';
import { AppConfig, container } from 'src/inversify.config';
import { NotificationDataStore, NotificationDataSummary } from 'src/stores/notifications-store';
import { getNotificationDisplayInformation } from './notifications-utils';
import { CustomGlobalThemeToken } from 'src/common/themes';
import { Trash2, Glasses } from 'lucide-react';
import { useSideMenu } from '..';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { ENV } from 'src';

const { Text, Paragraph } = Typography;

const { useToken } = theme;

export const NotificationCard = ({
  notification,
  onRemove,
  onClick,
  mode,
}: {
  notification: NotificationDataSummary;
  onRemove: (id: string) => void;
  onClick: () => void;
  mode: 'read' | 'unread';
}) => {
  const { token } = useToken();
  const customToken = token as CustomGlobalThemeToken;

  const { t, i18n } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);

  const displayInformation = useMemo(
    () => getNotificationDisplayInformation(notification, config.environment === ENV.DEV),
    [notification, i18n.language] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const navigate = useNavigate();
  const timestamp = useTimestampLabel(moment(notification.createdAt));
  const notificationsStore = container.get(NotificationDataStore);
  const [deletingNotification, setDeletingNotification] = useState(false);
  const [ellipsis, setEllipsis] = useState(true);
  const [textNeedsEllipsis, setTextNeedsEllipsis] = useState(false);
  const [optionsDropdownOpen, setOptionsDropdownOpen] = useState(false);
  const { setSelectedSideMenu } = useSideMenu();

  const updateSideMenuIndicator = () => {
    if (displayInformation.path?.includes('organizations')) {
      setSelectedSideMenu('organizations');
    } else if (displayInformation.path?.includes('licenses')) {
      setSelectedSideMenu('licensing');
    } else if (displayInformation.path?.includes('quarantine')) {
      setSelectedSideMenu('quarantine');
    }
  };

  return (
    <>
      <Card
        onContextMenu={(e) => {
          if (mode === 'unread') {
            e.preventDefault();
            e.stopPropagation();
            setOptionsDropdownOpen((prev) => !prev);
          }
        }}
        onClick={
          displayInformation.path
            ? () => {
                if (mode === 'unread') {
                  notificationsStore.markAsRead(notification.notificationId);
                  onRemove(notification.id);
                }
                navigate(displayInformation.path!, { state: displayInformation.navigationState });
                updateSideMenuIndicator();
                onClick();
              }
            : undefined
        }
        hoverable={displayInformation.path !== undefined}
        size="small"
        style={{
          userSelect: 'none',
          borderRadius: '2px',
          padding: 4,
          margin: '12px 18px 12px 8px',
          backgroundColor: customToken.containerCardBg,
          borderColor: customToken.containerCardBg,
        }}
      >
        <Card.Meta
          style={{ margin: '12px 18px 12px 8px' }}
          avatar={displayInformation.icon}
          title={
            <div
              style={{ width: '100%', display: 'flex', cursor: textNeedsEllipsis || displayInformation.path !== undefined ? 'pointer' : undefined }}
              onClick={(e) => {
                if (textNeedsEllipsis) {
                  e.preventDefault();
                  e.stopPropagation();
                  setEllipsis((prev) => !prev);
                } else if (displayInformation.path !== undefined) {
                  navigate(displayInformation.path!, { state: displayInformation.navigationState });
                  updateSideMenuIndicator();
                  if (mode === 'unread') {
                    notificationsStore.markAsRead(notification.notificationId);
                    onRemove(notification.id);
                  }
                  onClick();
                }
              }}
            >
              <span style={{ textWrap: 'initial' }}>{t(displayInformation.title)}</span>
              <span style={{ marginRight: 8, marginLeft: 'auto' }}>
                {
                  <Text type="secondary" style={{ fontSize: 'small', fontWeight: 450 }}>
                    {timestamp}
                  </Text>
                }
              </span>

              {textNeedsEllipsis && (
                <span style={{ marginLeft: 10, marginRight: 14, cursor: 'pointer' }}>
                  {ellipsis && <DownOutlined style={{ fontSize: 14, color: token.colorTextTertiary }} />}
                  {!ellipsis && <UpOutlined style={{ fontSize: 14, color: token.colorTextTertiary }} />}
                </span>
              )}

              {deletingNotification && <LoadingOutlined spin />}

              {mode === 'read' && (
                <>
                  {!deletingNotification && (
                    <span
                      style={{ cursor: 'pointer' }}
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDeletingNotification(true);
                        await notificationsStore.delete(notification.notificationId);
                        onRemove(notification.id);
                      }}
                    >
                      <Trash2 size={18} className="active-icon" style={{ color: customToken.actionIconColor }} />
                    </span>
                  )}
                </>
              )}

              {mode === 'unread' && !deletingNotification && (
                <>
                  <Dropdown
                    onOpenChange={(open) => setOptionsDropdownOpen(open)}
                    open={optionsDropdownOpen}
                    placement="bottomRight"
                    trigger={['click']}
                    arrow={false}
                    menu={{
                      items: [
                        {
                          key: 'read',
                          icon: <Glasses size={18} style={{ color: customToken.colorInfo, fontSize: 18 }} />,
                          onClick: async (menu) => {
                            menu.domEvent.preventDefault();
                            menu.domEvent.stopPropagation();
                            setDeletingNotification(true);
                            await notificationsStore.markAsRead(notification.notificationId);
                            onRemove(notification.id);
                          },
                          label: t('MarkAsRead'),
                        },
                        {
                          key: 'delete',
                          icon: <Trash2 size={18} style={{ color: customToken.primaryButtonRoundedBg, fontSize: 18 }} />,
                          onClick: async (menu) => {
                            menu.domEvent.preventDefault();
                            menu.domEvent.stopPropagation();
                            setDeletingNotification(true);
                            await notificationsStore.delete(notification.notificationId);
                            onRemove(notification.id);
                          },
                          label: t('Delete'),
                        },
                      ],
                    }}
                  >
                    <EllipsisOutlined
                      className="active-icon"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    />
                  </Dropdown>
                </>
              )}
            </div>
          }
          description={
            <Paragraph
              ellipsis={
                ellipsis
                  ? {
                      rows: 2,
                      onEllipsis() {
                        setTextNeedsEllipsis(true);
                      },
                    }
                  : false
              }
              type="secondary"
              style={{ fontWeight: '500', display: 'block', lineHeight: '18px' }}
            >
              {t(displayInformation.description)}
            </Paragraph>
          }
        />
      </Card>
    </>
  );
};
